import React from "react";
import { Box, Switch } from "@mui/joy";
import type { DropResult } from "@hello-pangea/dnd";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useTraining } from "../../../../contexts/api";
import { useAlert } from "../../../../contexts/app/notificationToast";
import type {
  TheoryTestTemplate,
  QuestionTemplateForm,
} from "../../../../api/theoryTests/TheoryTest.typed";
import type { TrainingTemplate } from "../../../../api/training/Training.typed";
import { extractDataFromResponse } from "../../../../utils/paginationHelpers";
import { SAddModule, SSave, SAddCriteria } from "../../../../styles/StyledMUI";
import DarkTypography from "../../../common/DarkTypography";
import IconButtonCTA from "../../../common/IconButtonCTA";
import ActionTooltip from "../../../common/ActionTooltip";
import QuestionFormCard from "./QuestionFormCard"; // External component
import { usePermissions, Permission } from "../../../../contexts/app/permissions";
import TrainingsEditorDockedActions from "../../common/TrainingsEditorDockedActions";
import TrainingsEmptyState from "../../common/TrainingsEmptyState";
import TrainingsQualificationSelect from "../../common/TrainingsQualificationSelect";
import TrainingsTemplateDashboardFrame from "../../common/TrainingsTemplateDashboardFrame";

export default function TheoryTestTemplateEditor() {
  const [templates, setTemplates] = React.useState<TrainingTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState<TrainingTemplate>();
  const [theoryTestTemplate, setTheoryTestTemplate] = React.useState<TheoryTestTemplate>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isDragging, setIsDragging] = React.useState<boolean>(false);

  const {
    getTrainingsTemplates,
    getTheoryTestTemplate,
    updateTheoryTestTemplate,
    createTheoryTestTemplate,
  } = useTraining();
  const { showAlert } = useAlert();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.TRAININGS_EDIT);

  React.useEffect(() => {
    setIsLoading(true);
    getTrainingsTemplates()
      .then((t) => setTemplates(extractDataFromResponse(t)))
      .finally(() => setIsLoading(false));
  }, [getTrainingsTemplates]);

  const handleChangeTemplate = async (newValue: string | null) => {
    const template = templates?.find((t) => t.ID.toString() === newValue);
    if (template) {
      setSelectedTemplate(template);
      setIsLoading(true);
      try {
        const testTemplate = await getTheoryTestTemplate(template.ID);
        // If the API returns 204, consider there is no template
        setTheoryTestTemplate(testTemplate || undefined);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleUpdateQuestion = (qIndex: number, updatedQuestion: QuestionTemplateForm) => {
    if (!theoryTestTemplate?.TheoryTest) return;

    // Keep CorrectAnswers consistent with PossibleAnswers
    const cleanedQuestion = {
      ...updatedQuestion,
      CorrectAnswers: updatedQuestion.PossibleAnswers.map((answer: string) =>
        updatedQuestion.CorrectAnswers.includes(answer) ? answer : "",
      ).filter((answer: string) => answer !== ""), // Remove empty entries
    };

    const updatedQuestions = [...theoryTestTemplate.TheoryTest];
    updatedQuestions[qIndex] = cleanedQuestion;
    setTheoryTestTemplate({
      ...theoryTestTemplate,
      TheoryTest: updatedQuestions,
    });
  };

  const handleDeleteQuestion = (qIndex: number) => {
    if (!theoryTestTemplate?.TheoryTest) return;
    const updatedQuestions = [...theoryTestTemplate.TheoryTest];
    updatedQuestions.splice(qIndex, 1);
    setTheoryTestTemplate({
      ...theoryTestTemplate,
      TheoryTest: updatedQuestions,
    });
  };

  const handleAddQuestion = () => {
    if (!theoryTestTemplate) return;
    const newQuestion: QuestionTemplateForm = {
      ID: Date.now(),
      QuestionText: "",
      PossibleAnswers: [""],
      CorrectAnswers: [], // Initialize with an empty array
    };
    setTheoryTestTemplate({
      ...theoryTestTemplate,
      TheoryTest: [...(theoryTestTemplate.TheoryTest ?? []), newQuestion],
    });
  };

  const onDragStart = () => {
    setIsDragging(true);
  };

  const onDragEnd = (result: DropResult) => {
    setIsDragging(false);
    if (!result.destination || !theoryTestTemplate?.TheoryTest) return;

    const updatedQuestions = [...theoryTestTemplate.TheoryTest];
    const [movedQuestion] = updatedQuestions.splice(result.source.index, 1);
    updatedQuestions.splice(result.destination.index, 0, movedQuestion);
    setTheoryTestTemplate({
      ...theoryTestTemplate,
      TheoryTest: updatedQuestions,
    });
  };

  const questionList = React.useMemo(
    () => theoryTestTemplate?.TheoryTest ?? [],
    [theoryTestTemplate?.TheoryTest],
  );

  const isTemplateValid = React.useMemo(() => {
    if (!theoryTestTemplate?.TheoryTest) return false;

    // Require at least one question
    if (theoryTestTemplate.TheoryTest.length === 0) return false;

    return theoryTestTemplate.TheoryTest.every(
      (question: QuestionTemplateForm) =>
        question.CorrectAnswers.length > 0 && // At least one correct answer
        question.QuestionText.trim() !== "" && // Non-empty question text
        question.PossibleAnswers.every((answer: string) => answer.trim() !== ""), // No empty answers
    );
  }, [theoryTestTemplate?.TheoryTest]);

  const handleSaveTemplate = () => {
    if (!theoryTestTemplate) return;

    if (theoryTestTemplate.TheoryTest.length === 0) {
      showAlert("Impossible de sauvegarder : aucune question n'a été créée", "danger");
      return;
    }

    if (!isTemplateValid) {
      showAlert("Veuillez remplir toutes les réponses correctes", "danger");
      return;
    }

    updateTheoryTestTemplate(theoryTestTemplate)
      .then(() => showAlert("QCM sauvegardé avec succès", "success"))
      .catch(() => showAlert("Erreur lors de la sauvegarde du QCM", "danger"));
  };

  const handleCreateTemplate = async () => {
    if (selectedTemplate) {
      const newTemplate: TheoryTestTemplate = {
        ID: selectedTemplate.ID,
        TrainingTemplateID: selectedTemplate.ID,
        TheoryTest: [],
        Online: false,
      };
      setIsLoading(true);
      try {
        await createTheoryTestTemplate(newTemplate);
        setTheoryTestTemplate(newTemplate);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleToggleOnline = () => {
    if (!theoryTestTemplate) return;

    // Require questions before going online
    if (theoryTestTemplate.TheoryTest.length === 0) {
      showAlert("Impossible de mettre en ligne : aucune question n'a été créée", "danger");
      return;
    }

    // Require the template to be valid before going online
    if (!isTemplateValid) {
      showAlert("Impossible de mettre en ligne : certaines questions sont incomplètes", "danger");
      return;
    }

    const updatedTemplate = {
      ...theoryTestTemplate,
      Online: !theoryTestTemplate.Online,
    };
    setTheoryTestTemplate(updatedTemplate);
    updateTheoryTestTemplate(updatedTemplate);
  };

  return (
    <TrainingsTemplateDashboardFrame
      isLoading={isLoading}
      headerLeft={
        <TrainingsQualificationSelect
          templates={templates}
          value={selectedTemplate?.ID?.toString() || ""}
          onChange={(newValue) => void handleChangeTemplate(newValue)}
          emptyStateDisabledOptionLabel="Aucune formation disponible"
          trailingInGroup={
            theoryTestTemplate ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DarkTypography sx={{ fontSize: "15px" }}>En ligne</DarkTypography>
                <Switch
                  checked={theoryTestTemplate.Online}
                  onChange={handleToggleOnline}
                  color={theoryTestTemplate.Online ? "success" : "danger"}
                  slotProps={{
                    track: {
                      sx: {
                        backgroundColor: theoryTestTemplate.Online ? undefined : "#ff0000",
                      },
                    },
                  }}
                />
              </Box>
            ) : null
          }
        />
      }
      headerRight={
        !isLoading && theoryTestTemplate === undefined && selectedTemplate?.ID ? (
          <ActionTooltip title="Créer un nouveau QCM" disabled={!canEdit}>
            <IconButtonCTA
              ctaVariant="accent"
              aria-label="Créer un nouveau QCM"
              onClick={handleCreateTemplate}
              disabled={!canEdit}
            >
              <SAddCriteria />
            </IconButtonCTA>
          </ActionTooltip>
        ) : null
      }
    >
      {theoryTestTemplate ? (
        <Box sx={{ width: "100%" }}>
          {/* Quiz content */}
          {theoryTestTemplate.TheoryTest.length > 0 ? (
            <Box sx={{ mt: 2, opacity: isDragging ? 0.8 : 1 }}>
              <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                <Droppable droppableId="questions">
                  {(provided, snapshot) => (
                    <Box
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      sx={{ opacity: snapshot.isDraggingOver ? 0.9 : 1 }}
                    >
                      {questionList.map((question: QuestionTemplateForm, qIndex: number) =>
                        question.ID ? (
                          <Draggable
                            key={question.ID.toString()}
                            draggableId={`question-${question.ID}`}
                            index={qIndex}
                          >
                            {(provided) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <QuestionFormCard
                                  question={question}
                                  onChange={(updatedQuestion) =>
                                    handleUpdateQuestion(qIndex, updatedQuestion)
                                  }
                                  onDelete={() => handleDeleteQuestion(qIndex)}
                                />
                              </Box>
                            )}
                          </Draggable>
                        ) : null,
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>
          ) : (
            <TrainingsEmptyState
              dense
              message="Commencez à créer vos questions en cliquant sur le bouton +"
            />
          )}

          <TrainingsEditorDockedActions>
            <ActionTooltip title="Ajouter une question" disabled={!canEdit}>
              <IconButtonCTA
                ctaVariant="accent"
                aria-label="Ajouter une question"
                sx={{ marginRight: 2 }}
                onClick={handleAddQuestion}
                disabled={!canEdit}
              >
                <SAddModule />
              </IconButtonCTA>
            </ActionTooltip>
            <ActionTooltip
              title={
                !isTemplateValid ? "Veuillez remplir toutes les réponses correctes" : "Sauvegarder"
              }
              disabled={!canEdit || !isTemplateValid}
            >
              <IconButtonCTA
                ctaVariant="success"
                aria-label="Sauvegarder"
                onClick={handleSaveTemplate}
                disabled={!canEdit || !isTemplateValid}
              >
                <SSave />
              </IconButtonCTA>
            </ActionTooltip>
          </TrainingsEditorDockedActions>
        </Box>
      ) : selectedTemplate !== undefined && theoryTestTemplate === undefined ? (
        <TrainingsEmptyState message="Aucun QCM pour cette formation" />
      ) : (
        <TrainingsEmptyState message="Sélectionnez une formation pour charger le QCM correspondant." />
      )}
    </TrainingsTemplateDashboardFrame>
  );
}
